import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { LinkButton } from '../../ui/LinkButton/LinkButton';

interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
    value: string;
    onChange: (value: string) => void;
    chooseDateLabel?: string;
    // Compact mode hides the inline "choose date" button (for tight layouts) and opens
    // the calendar when the input is focused instead.
    compact?: boolean;
}

function parseISO(str: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
}

function toISO(year: number, month: number, day: number): string {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export const DateInput: React.FC<DateInputProps> = ({
    value,
    onChange,
    chooseDateLabel,
    disabled,
    compact = false,
    className = '',
    ...inputProps
}) => {
    const { t } = useTranslation();
    const months = t(TranslationKey.DateInput.Months, { returnObjects: true }) as string[];
    const dayHeaders = t(TranslationKey.DateInput.DayHeaders, { returnObjects: true }) as string[];
    const resolvedChooseDateLabel = chooseDateLabel ?? t(TranslationKey.DateInput.ChooseDate);
    const parsed = parseISO(value);
    const [open, setOpen] = useState(false);
    const [viewYear, setViewYear] = useState(parsed?.getFullYear() ?? new Date().getFullYear());
    const [viewMonth, setViewMonth] = useState(parsed?.getMonth() ?? new Date().getMonth());
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

    const syncViewToValue = () => {
        if (parsed) {
            setViewYear(parsed.getFullYear());
            setViewMonth(parsed.getMonth());
        }
    };

    const handleToggle = () => {
        syncViewToValue();
        setOpen((o) => !o);
    };

    const handleInputFocus = () => {
        if (!compact || disabled) return;
        syncViewToValue();
        setOpen(true);
    };

    const handleDayClick = (day: number) => {
        onChange(toISO(viewYear, viewMonth, day));
        setOpen(false);
    };

    const handlePrev = () => {
        if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); }
        else setViewMonth((m) => m - 1);
    };

    const handleNext = () => {
        if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); }
        else setViewMonth((m) => m + 1);
    };

    const selectedDate = parseISO(value);

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div className="flex items-center gap-m">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder={t(TranslationKey.DateInput.Placeholder)}
                    disabled={disabled}
                    className="h-9 flex-1 min-w-0 px-m rounded-md border border-line bg-primary-50 text-primary-1225 text-compact placeholder:text-primary-600 focus:outline-none focus:border-focus focus:ring-4 focus:ring-focus-ring transition-colors"
                    {...inputProps}
                />
                {!disabled && !compact && (
                    <LinkButton onClick={handleToggle} className="whitespace-nowrap">
                        {resolvedChooseDateLabel}
                    </LinkButton>
                )}
            </div>

            {open && (
                <div className="absolute z-50 top-full mt-xs left-0 w-64 rounded-sm border border-line-subtle bg-primary-50 shadow-medium p-m">
                    {/* Navigation header */}
                    <div className="flex items-center justify-between mb-s">
                        <button
                            type="button"
                            onClick={handlePrev}
                            className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-primary-200 text-primary-1000 cursor-pointer text-l leading-none"
                        >
                            ‹
                        </button>
                        <span className="text-compact font-semibold text-primary-1200">
                            {months[viewMonth]} {viewYear}
                        </span>
                        <button
                            type="button"
                            onClick={handleNext}
                            className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-primary-200 text-primary-1000 cursor-pointer text-l leading-none"
                        >
                            ›
                        </button>
                    </div>

                    {/* Day-of-week headers */}
                    <div className="grid grid-cols-7 mb-xs">
                        {dayHeaders.map((d) => (
                            <div key={d} className="text-center text-xs text-primary-700 py-xs">
                                {d}
                            </div>
                        ))}
                    </div>

                    {/* Day grid */}
                    <div className="grid grid-cols-7">
                        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                            <div key={`empty-${i}`} />
                        ))}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const isSelected =
                                selectedDate?.getFullYear() === viewYear &&
                                selectedDate?.getMonth() === viewMonth &&
                                selectedDate?.getDate() === day;
                            return (
                                <button
                                    key={day}
                                    type="button"
                                    onClick={() => handleDayClick(day)}
                                    className={`text-center text-compact py-xs rounded-sm transition-colors cursor-pointer
                                        ${isSelected
                                            ? 'bg-accent-400 text-primary-50 font-semibold'
                                            : 'hover:bg-primary-200 text-primary-1200'
                                        }`}
                                >
                                    {day}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
